import { DateTime } from 'luxon';
import * as buttons from 'smg/buttons';
import { GetLineItems } from 'smg/customer-api/lineitems';
import { DownloadQuoteInvoice, OpenQuotes, QuoteRefQuery } from 'smg/customer-api/v2/quotes';
import { ShowError } from 'smg/errors';
import * as models from 'smg/customer-api/models';
import * as formatting from 'smg/formatting';
import * as html from 'smg/html';
import { Linkify } from 'smg/linkify';
import { IsImageURL, Picture } from 'smg/picture';
import { GetProofURLs } from 'smg/proofs';
import QueryString from 'smg/querystring';
import { SanitizeRichHTML } from 'smg/richtext';
import * as template from 'smg/template';
import * as carts from '../carts';
import { Relref } from '../hugo';
import * as lineitems from '../lineitems';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

main();

function main() {
    const container = document.querySelector('#quotes-container') as HTMLElement;
    const spinner = document.querySelector('#quotes-spinner') as HTMLElement;
    const searchParent = document.querySelector('#search-parent') as HTMLElement;
    const searchEl = document.querySelector('#quote-search') as HTMLInputElement;

    const currQuery = QueryString().get('q');
    let q: string = currQuery || '';
    const quoteID = QueryString().get('id');
    const quoteSentInstanceID = QueryString().get('instance');

    const showSearch = () => {
        if (currQuery) {
            searchEl.value = currQuery;
        }

        searchParent.classList.remove('d-none');
        spinner.parentElement?.removeChild(spinner);
    };

    if (quoteID) {
        if (quoteID.length === 43) { // key
            q = `key:${quoteID}`;
        } else if (quoteID.length === 38) { // hash
            q = `sha3224:${quoteID}`;
        } else {
            q = `id:${quoteID}`;
        }
    } else if (quoteSentInstanceID) {
        if (quoteSentInstanceID.length === 43) { // key
            q = `quoteSentKey:${quoteSentInstanceID}`;
        } else {
            q = `quoteSentInstanceID:${quoteSentInstanceID}`;
        }
    } else if (!q) {
        showSearch();
        return;
    }

    OpenQuotes({
        sort: 'created asc',
        max: 100,
        data: 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteOrders,quoteRequest,key,quoteSents',
        q,
    }).then(async data => {
        if (!data.results.length) {
            if (currQuery) {
                const emptySearchWarning = document.querySelector('#empty-search-warning') as HTMLElement;
                emptySearchWarning.classList.remove('d-none');
            }

            showSearch();
            return;
        }

        const backToSearch = document.querySelector('#back-to-search') as HTMLElement;
        if (!currQuery) {
            backToSearch.parentElement?.removeChild(backToSearch);
        }

        interface quoteCard {
            quoteSent: models.QuoteSent | undefined;
            el: DocumentFragment;
        }

        const quoteCards: quoteCard[] = [];

        for (const quote of data.results) {
            const el = template.Clone('#quote-template');

            const serial = el.querySelector('.quote-serial') as HTMLElement;
            serial.textContent = String(quote.serial);

            const summary = el.querySelector('.quote-summary') as HTMLElement;
            summary.appendChild(await lineitems.Table(quote.quoteLineItems));

            const proofsCount = quote.quoteProofs.length;

            const proofsContainer = el.querySelector('.quote-proofs-container') as HTMLElement;
            const revisionLink = el.querySelector('.quote-request-revision') as HTMLAnchorElement;
            const proofsParent = el.querySelector('.quote-proofs-parent') as HTMLElement;
            if (quote.quoteProofs.length) {
                for (const op of quote.quoteProofs) {
                    const p = op.proof;
                    if (!p) {
                        continue;
                    }

                    const urls = GetProofURLs(window.cloudFront, p.file || '');

                    const el = template.Clone('#quote-proof-template').firstElementChild as HTMLElement;

                    const a = el.querySelector('a') as HTMLAnchorElement;
                    a.href = urls.main;
                    a.setAttribute('data-fancybox', quote.id || '');

                    const img = a.querySelector('img') as HTMLImageElement;
                    img.src = urls.main;
                    img.alt = op.name || '';

                    const $proofName = el.querySelector('.proof-name') as HTMLElement | null;
                    if (op.name) {
                        if ($proofName) {
                            $proofName.textContent = op.name;
                            $proofName.title = op.name;
                        }
                    } else {
                        $proofName?.remove();
                    }

                    proofsParent.appendChild(el);
                }

                revisionLink.href = `${Relref('/revisions')}?id=${quoteID || quote.key}`;
            }

            const invoiceDownload = el.querySelector('.quote-invoice-download') as HTMLButtonElement;
            invoiceDownload.addEventListener('click', () => {
                buttons.Start(invoiceDownload);
                void DownloadQuoteInvoice(QuoteRefQuery(quote.key || quote.id || '')).catch(ShowError).finally(() => buttons.Stop(invoiceDownload));
            });

            const addToCart = el.querySelector('.quote-add-to-cart') as HTMLButtonElement;
            addToCart.addEventListener('click', async () => {
                buttons.Start(addToCart);
                await carts.AddCartItem({ quoteID: quote.key }, {
                    promptToCheckout: data.results.length !== 1,
                }).then(() => {
                    addToCart.classList.remove('btn-primary', 'btn-success', 'text-white');
                    addToCart.classList.add('btn-outline-success');

                    addToCart.querySelector('i')?.classList.remove('fa-shopping-cart');
                    addToCart.querySelector('i')?.classList.add('fa-check');
                }).finally(() => {
                    buttons.Stop(addToCart);
                    if (data.results.length === 1) {
                        window.location.href = Relref('/cart');
                    }
                });
            });

            const cardBody = el.querySelector('.card-body') as HTMLElement;

            if (proofsCount) {
                cardBody.classList.add('row-cols-lg-2');

                revisionLink.href = `${Relref('/revisions')}?id=${quoteID || quote.key}`;

                if (proofsCount > 1) {
                    proofsParent.classList.add('row-cols-lg-2');
                }
            } else {
                proofsContainer.parentElement?.removeChild(proofsContainer);
                revisionLink.parentElement?.removeChild(revisionLink);
            }

            // The quote's most recent quote-sent decides which send group the
            // quote lands in (falling back to the row id on very old rows with
            // no instance id).
            let quoteSent: models.QuoteSent | undefined;
            let quoteSentMs = -1;
            for (const candidate of quote.quoteSents) {
                const ms = candidate.created?.isValid ? candidate.created.toMillis() : 0;
                if (ms > quoteSentMs) {
                    quoteSentMs = ms;
                    quoteSent = candidate;
                }
            }

            quoteCards.push({ quoteSent, el });
        }

        // Group the quotes by the quote-sent instance that covered them — most
        // sends never reach the customer as an email, so the message card
        // above each group is the only place the rep's message is visible.
        interface quoteGroup {
            key: string;
            quoteSent: models.QuoteSent;
            ms: number;
            cards: DocumentFragment[];
        }

        const groups = new Map<string, quoteGroup>();
        const unsentCards: DocumentFragment[] = [];

        for (const card of quoteCards) {
            if (!card.quoteSent) {
                unsentCards.push(card.el);
                continue;
            }

            const key = card.quoteSent.quoteSentInstanceID || card.quoteSent.id || '';
            const ms = card.quoteSent.created?.isValid ? card.quoteSent.created.toMillis() : 0;

            const group = groups.get(key);
            if (group) {
                group.cards.push(card.el);
                if (ms > group.ms) {
                    group.ms = ms;
                    group.quoteSent = card.quoteSent;
                }
            } else {
                groups.set(key, { key, quoteSent: card.quoteSent, ms, cards: [card.el] });
            }
        }

        const sortedGroups = [...groups.values()].sort((a, b) => b.ms - a.ms);

        const appendQuoteSentCard = (group: quoteGroup, older: boolean) => {
            const quoteSent = group.quoteSent;
            const quoteSentContent = quoteSent.content?.trim() ?? '';
            const attachments = quoteSent.attachments.filter(attachment => attachment.publicURL);

            // A send with no message still earns a card when the rep attached
            // files to it or wrote a subject — the subject often carries the
            // send's intent (e.g. preliminary vs action needed).
            if (!quoteSentContent && !(quoteSent.subject?.trim() ?? '') && !attachments.length) {
                return;
            }

            const el = template.Clone('#quote-sent-template');

            const sender = el.querySelector('.quote-sent-sender') as HTMLElement;
            const firstName = quoteSent.user?.firstName?.trim() ?? '';
            sender.textContent = firstName ? `A message from ${firstName}` : 'A message from our team';

            const subject = el.querySelector('.quote-sent-subject') as HTMLElement;
            subject.textContent = quoteSent.subject?.trim() || 'Your Quote Is Ready';

            const date = el.querySelector('.quote-sent-date') as HTMLElement;
            if (quoteSent.created?.isValid) {
                date.textContent = quoteSent.created.toLocaleString(DateTime.DATETIME_MED);
            } else {
                date.remove();
            }

            const body = el.querySelector('.quote-sent-body') as HTMLElement;
            if (quoteSentContent) {
                if (quoteSent.contentType === 'text/html') {
                    body.innerHTML = Linkify(SanitizeRichHTML(quoteSentContent));
                } else {
                    body.innerHTML = Linkify(html.Nl2br(html.Escape(quoteSentContent)));
                }
            } else {
                body.remove();
            }

            if (attachments.length) {
                const attachmentsParent = el.querySelector('.quote-sent-attachments') as HTMLElement;
                attachmentsParent.classList.remove('d-none');
                if (quoteSentContent) {
                    attachmentsParent.classList.add('mt-3');
                }

                const imagesParent = el.querySelector('.quote-sent-attachments-images') as HTMLElement;
                const filesParent = el.querySelector('.quote-sent-attachments-files') as HTMLElement;

                for (const attachment of attachments) {
                    const url = attachment.publicURL;
                    const name = attachment.name.trim() || 'Attachment';

                    if (IsImageURL(name) || IsImageURL(url)) {
                        // data-fancybox must be on the anchor (and the anchor
                        // around the img) before Picture() runs — its
                        // fancyboxThumb option walks up to it.
                        const a = document.createElement('a');
                        a.href = url;
                        a.setAttribute('data-fancybox', `quote-sent-${group.key}`);
                        a.setAttribute('data-caption', name);
                        a.classList.add('quote-sent-attachment-thumb', 'border', 'rounded', 'overflow-hidden');

                        const img = document.createElement('img');
                        img.alt = name;
                        img.title = name;
                        a.appendChild(img);
                        imagesParent.appendChild(a);

                        void Picture(img, url, { displayWidth: 72, alt: name, title: name, fancyboxThumb: true });
                    } else {
                        const a = document.createElement('a');
                        a.href = url;
                        a.target = '_blank';
                        a.rel = 'noopener';
                        a.classList.add('quote-sent-attachment-file', 'text-decoration-none');

                        const chip = document.createElement('span');
                        chip.classList.add('d-inline-flex', 'align-items-center', 'gap-2', 'border', 'rounded', 'px-2', 'py-1', 'small');

                        const icon = document.createElement('i');
                        icon.classList.add('fas', 'fa-file-alt');

                        const fileName = document.createElement('span');
                        fileName.textContent = name;

                        chip.appendChild(icon);
                        chip.appendChild(fileName);

                        if (attachment.size) {
                            const fileSize = document.createElement('span');
                            fileSize.classList.add('text-muted', 'text-nowrap');
                            fileSize.textContent = formatting.HumanFileSize(attachment.size);
                            chip.appendChild(fileSize);
                        }

                        a.appendChild(chip);
                        filesParent.appendChild(a);
                    }
                }

                if (!imagesParent.children.length) {
                    imagesParent.remove();
                }
                if (!filesParent.children.length) {
                    filesParent.remove();
                }
            }

            if (older) {
                (el.firstElementChild as HTMLElement).classList.add('bg-light');
            }

            container.appendChild(el);
        };

        // A dated pill between two send groups keeps one send's quotes from
        // reading as the previous send's — it shows even when the group below
        // it has no message card.
        const appendGroupBoundary = (text: string, sent: boolean) => {
            const el = template.Clone('#quote-group-boundary-template');
            if (!sent) {
                el.querySelector('.quote-group-boundary-label i')?.remove();
            }
            const label = el.querySelector('.quote-group-boundary-text') as HTMLElement;
            label.textContent = text;
            container.appendChild(el);
        };

        const [firstGroup, ...olderGroups] = sortedGroups;
        if (firstGroup) {
            appendQuoteSentCard(firstGroup, false);

            for (const card of firstGroup.cards) {
                container.appendChild(card);
            }

            for (const group of olderGroups) {
                const date = group.quoteSent.created;
                if (date?.isValid) {
                    appendGroupBoundary(`Sent ${date.toLocaleString(DateTime.DATE_MED)}`, true);
                } else {
                    appendGroupBoundary('Sent', true);
                }

                appendQuoteSentCard(group, true);

                for (const card of group.cards) {
                    container.appendChild(card);
                }
            }
        }

        if (unsentCards.length) {
            if (sortedGroups.length) {
                appendGroupBoundary('Other quotes', false);
            }

            for (const card of unsentCards) {
                container.appendChild(card);
            }
        }

        spinner.parentElement?.removeChild(spinner);
        container.classList.remove('d-none');
    }).catch(err => {
        showSearch();
        throw err;
    });

    void GetLineItems();
}